import { Box, Button, Stack } from "@mui/material";
import React, { useState } from "react";

import { ImageCanvas } from "components/common/ImageCanvas";
import { useReviewPage } from "context/reviewPageContext/reviewPageContext";
import { CopyButton } from "new-components/buttons/CopyButton";
import { Typography } from "new-components/typography/Typography";
import { Disagreement } from "types/disagreements";
import { stringClassNameMap } from "utils/disagreementsUtils";
import { generateImageUrl } from "utils/generateImageUrl";

import disagreementConfirmSvg from "../../pages/review/assets/disagreement-confirm.svg";

import { DisagreementNoteModal } from "./components/DisagreementNoteModal";
import { ButtonPressedEnum } from "./types";

export function DisagreementsContent() {
  const [openDisagreementNoteDialog, setOpenDisagreementNoteDialog] = useState<boolean>(false);
  const {
    currentImageQuery,
    disagreementCheckResponse: { challengedAnswer, referenceAnswer },
    setDisagreementCheckResponse,
  } = useReviewPage();

  const disagreementData = currentImageQuery as Disagreement;

  const handleOpenDisagreementNoteDialog = () => {
    setOpenDisagreementNoteDialog(true);
  };

  const handleCloseDisagreementNoteDialog = () => {
    setOpenDisagreementNoteDialog(false);
  };

  const handleKeepMyAnswer = async () => {
    handleOpenDisagreementNoteDialog();
  };

  const handleChangeMyAnswer = async () => {
    setDisagreementCheckResponse((prev) => ({
      ...prev,
      isDisagreement: false,
    }));
  };

  return (
    <>
      <Stack
        id="disagreements-page"
        sx={{
          gap: "10px",
          height: "calc(100% - 50px)",
          paddingTop: "10px",
          paddingBottom: { xs: "10px", sm: "20px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#00C7D9",
            padding: "5px 20px",
            gap: "14px",
            borderRadius: "10px",
            height: "auto",
          }}
        >
          <img
            src={disagreementConfirmSvg}
            style={{ width: "24px", height: "24px", objectFit: "contain" }}
            alt="charged img"
          />
          <Stack sx={{ gap: "4px", color: "white" }}>
            <Typography sx={{ fontSize: "18px", lineHeight: "24px", fontWeight: "600" }}>Are you sure?</Typography>
            <Typography sx={{ fontSize: "14px", lineHeight: "20px" }}>
              Your answer is different from another answer. Please take a look at both answers and confirm that you want
              to keep your answer.
            </Typography>
          </Stack>
        </Box>

        <Stack
          sx={{
            flexGrow: 1,
            backgroundColor: "#E5E9ED",
            borderRadius: "20px",
            boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
          }}
        >
          <Box sx={{ display: "flex", gap: "20px", padding: "10px 20px" }}>
            <Stack sx={{ flex: 1, alignItems: "center", gap: "10px" }}>
              <Typography sx={{ fontSize: "20px", lineHeight: "22.5px", letterSpacing: "0.5px" }}>
                Your answer:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  backgroundColor: "white",
                  padding: "14px 0px",
                  borderRadius: "4px",
                  color: "black",
                  fontSize: "20px",
                  lineHeight: "24px",
                  letterSpacing: "0.5px",
                  fontWeight: "600",
                  textTransform: "none",
                }}
              >
                {stringClassNameMap[disagreementData?.challenged_answer?.class_name || ""] || challengedAnswer}
              </Box>
            </Stack>

            <Stack sx={{ flex: 1, alignItems: "center", gap: "10px" }}>
              <Typography sx={{ fontSize: "20px", lineHeight: "22.5px", letterSpacing: "0.5px" }}>
                Other answer:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  backgroundColor: "white",
                  padding: "14px 0px",
                  borderRadius: "4px",
                  color: "black",
                  fontSize: "20px",
                  lineHeight: "24px",
                  letterSpacing: "0.5px",
                  fontWeight: "600",
                  textTransform: "none",
                }}
              >
                {stringClassNameMap[disagreementData?.reference_answer?.class_name || ""] || referenceAnswer}
              </Box>
            </Stack>
          </Box>

          <Stack sx={{ flexGrow: 1, width: "100%", minHeight: "300px" }}>
            <ImageCanvas
              imageUrl={generateImageUrl(disagreementData?.iq_id || currentImageQuery?.id || "disagreement")}
              iqId={disagreementData?.iq_id || currentImageQuery?.id || "disagreement"}
              style={{ width: "100%", height: "100%" }}
            />
          </Stack>

          <Box sx={{ padding: { xs: "20px", sm: "33px 80px" } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-around",
                gap: "20px",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="contained"
                onClick={handleKeepMyAnswer}
                sx={{
                  width: { xs: "100%", sm: "470px" },
                  backgroundColor: "#0B72D9",
                  borderRadius: "22px",
                  padding: "5px 0px",
                  color: "white",
                  fontSize: "20px",
                  lineHeight: "24px",
                  fontWeight: "500",
                  textTransform: "none",
                  minWidth: "120px",
                }}
              >
                Keep my answer
              </Button>

              <Button
                variant="contained"
                onClick={handleChangeMyAnswer}
                sx={{
                  width: { xs: "100%", sm: "470px" },
                  backgroundColor: "#0B72D9",
                  borderRadius: "22px",
                  padding: "5px 0px",
                  color: "white",
                  fontSize: "20px",
                  lineHeight: "24px",
                  fontWeight: "500",
                  textTransform: "none",
                  minWidth: "120px",
                }}
              >
                Change my answer
              </Button>
            </Box>
          </Box>
        </Stack>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            color: "#919599",
            height: "auto",
          }}
        >
          <CopyButton
            iconClassName="h-[12px] w-[12px]"
            buttonClassName="h-[20px] normal-case"
            colorIcon="#C8CDD3"
            valueToCopy={currentImageQuery?.predictor.id || disagreementData?.predictor?.id || ""}
            text={`Detector ID: ${currentImageQuery?.predictor.id || disagreementData?.predictor?.id}`}
          />
          <CopyButton
            iconClassName="h-[12px] w-[12px]"
            buttonClassName="h-[20px] normal-case"
            colorIcon="#C8CDD3"
            valueToCopy={currentImageQuery?.id || disagreementData?.iq_id || ""}
            text={`Query ID: ${currentImageQuery?.id || disagreementData?.iq_id}`}
          />
        </Box>
      </Stack>

      {openDisagreementNoteDialog && (
        <DisagreementNoteModal
          imageUrl={generateImageUrl(disagreementData?.iq_id || currentImageQuery?.id || "disagreement")}
          iqId={disagreementData?.iq_id || currentImageQuery?.id || "disagreement"}
          buttonPressed={ButtonPressedEnum.KEEP}
          openModal={openDisagreementNoteDialog}
          onClose={handleCloseDisagreementNoteDialog}
        />
      )}
    </>
  );
}
